import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Divider,
  Chip,
} from '@mui/material';
import { BarChart3, Layers } from 'lucide-react';
import { Button, Drawer } from '../../../ui';
import { CandibankJobApplicantRequest, Skill } from '../../../utils/types/JobOpenings';
import SkillWeighting from '../../job_openings/create/ApplicantAnalysis/UI/SkillWeighting';

export interface FilterOptions {
  skills: string[];
  locations: string[];
  experienceLevels: string[];
  status: string[];
  matchRange: [number, number];
  availability: string[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  weightingEnabled: boolean;
  setWeightingEnabled: (value: (((prevState: boolean) => boolean) | boolean)) => void;
  extractedSkill: Skill[];
  setExtractedSkill: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void;
  similarToggle: boolean;
  setSimilarToggle: (value: (((prevState: boolean) => boolean) | boolean)) => void;
  extractedJobTitles: Skill[];
  setExtractedJobTitles: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void;
  similarJobTitle: boolean;
  setSimilarJobTitle: (value: (((prevState: boolean) => boolean) | boolean)) => void;
  extractedBehavioralScore: Skill[];
  setExtractedBehavioralScore: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void;
  similarBehavior: boolean;
  setSimilarBehavior: (value: (((prevState: boolean) => boolean) | boolean)) => void;
  keywordToggle: boolean;
  setKeywordToggle: (value: (((prevState: boolean) => boolean) | boolean)) => void;
  keywordWeightage: boolean;
  setKeywordWeightage: (value: (((prevState: boolean) => boolean) | boolean)) => void;
  extractedKeywords: Skill[];
  setExtractedKeywords: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void;
  searchObject: CandibankJobApplicantRequest;
  setSearchObject: (value: (((prevState: CandibankJobApplicantRequest) => CandibankJobApplicantRequest) | CandibankJobApplicantRequest)) => void;
  genderList: string[];
  setGenderList: (value: ((prevState: string[]) => string[]) | string[]) => void;
  minMaxAge: number[];
  setMinMaxAge: (minMax: number[]) => void;
  ageToggle: boolean;
  setAgeToggle: (value: (((prevState: boolean) => boolean) | boolean)) => void;
  setUseSkillPercentage: (value: (((prevState: boolean) => boolean) | boolean)) => void;
  statusList: string[];
  loading: boolean;
  setLoading: (value: (((prevState: boolean) => boolean) | boolean)) => void;
}

export const GraphFilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  extractedSkill,
  setExtractedSkill,
  similarToggle,
  setSimilarToggle,
  weightingEnabled,
  setWeightingEnabled,
  extractedJobTitles,
  setExtractedJobTitles,
  similarJobTitle,
  setSimilarJobTitle,
  extractedBehavioralScore,
  setExtractedBehavioralScore,
  similarBehavior,
  setSimilarBehavior,
  keywordToggle,
  setKeywordToggle,
  keywordWeightage,
  setKeywordWeightage,
  extractedKeywords,
  setExtractedKeywords,
  searchObject,
  setSearchObject,
  genderList,
  minMaxAge,
  ageToggle,
  setUseSkillPercentage,
  statusList,
  loading,
}) => {
  const handleApplyChange = async () => {
    const jobTitles = extractedJobTitles.map(item => item.skill);
    const behavioralScore = extractedBehavioralScore.map(item => item.skill);
    const keywords = extractedKeywords.map(({ skill, weight }) => ({
      keyword: skill,
      weight: weight,
    }));
    const cleanedSkills = extractedSkill.map(({ skill, weight }) => ({
      skill,
      weight,
    }));

    setUseSkillPercentage(weightingEnabled);

    const data = {
      ...searchObject,
      age: {
        ...searchObject.age,
        minAge: minMaxAge[0],
        maxAge: minMaxAge[1],
        includeUnknown: ageToggle,
      },
      additionalSkills: {
        skillWeightage: weightingEnabled,
        similar: similarToggle,
        skills: cleanedSkills.length === 0 ? [] : cleanedSkills,
      },
      jobTitle: {
        similar: similarJobTitle,
        jobTitles: jobTitles.length === 0 ? [] : jobTitles,
      },
      keywords: {
        keywordWeightage: keywordWeightage,
        similar: keywordToggle,
        keywords: keywords.length === 0 ? [] : keywords,
      },
      behavioralAttributes: {
        similar: similarBehavior,
        behavioralAttributes: behavioralScore.length === 0 ? [] : behavioralScore,
      },
      gender: genderList,
      applicantStatus: statusList,
    };

    setSearchObject(data);
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ width: '100%' }}>
          <Button label="Apply" onClick={() => handleApplyChange()} variant="primary" />
          <Button label="Close" onClick={onClose} variant="secondary" disabled={loading} />
        </Stack>
      }
      title="Apply Filters"
      width="w-full md:w-[740px] lg:w-[800px]"
    >
      <Stack spacing={2}>
        <Box
          component="section"
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'grey.200',
            p: { xs: 2, sm: 2.5 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.5,
                bgcolor: 'success.50',
                color: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BarChart3 size={16} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.primary', display: 'block' }}>
                Graph Axis Filters
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                Configure 3D axes, importance weighting, and term matching
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            {/* Skill(s) */}
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: loading ? 0.6 : 1 }}>
              <SkillWeighting
                title="X: Skill(s)"
                attributeName="Skill"
                isWeightRequired={true}
                extractedSkill={extractedSkill}
                setExtractedSkill={setExtractedSkill}
                weightingEnabled={weightingEnabled}
                setWeightingEnabled={setWeightingEnabled}
                similarEnabled={similarToggle}
                setSimilarEnabled={setSimilarToggle}
              />
            </Box>

            {/* Experience Job Titles */}
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: loading ? 0.6 : 1 }}>
              <SkillWeighting
                title="Y: Experience Job Title(s)"
                attributeName="job title"
                extractedSkill={extractedJobTitles}
                setExtractedSkill={setExtractedJobTitles}
                weightingEnabled={weightingEnabled}
                setWeightingEnabled={setWeightingEnabled}
                similarEnabled={similarJobTitle}
                setSimilarEnabled={setSimilarJobTitle}
              />
            </Box>

            {/* Separator */}
            <Box sx={{ gridColumn: '1 / -1', my: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Divider sx={{ flexGrow: 1 }} />
              <Chip
                icon={<Layers size={14} color="#059669" />}
                label="3D Graph Extended Axes"
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.625rem',
                  textTransform: 'uppercase',
                  bgcolor: 'grey.50',
                  borderColor: 'grey.200',
                  color: 'text.secondary',
                }}
              />
              <Divider sx={{ flexGrow: 1 }} />
            </Box>

            {/* Behavioral Attributes */}
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: loading ? 0.6 : 1 }}>
              <SkillWeighting
                title="Z: Behavioral Attribute(s)"
                attributeName="attribute"
                extractedSkill={extractedBehavioralScore}
                setExtractedSkill={setExtractedBehavioralScore}
                weightingEnabled={weightingEnabled}
                setWeightingEnabled={setWeightingEnabled}
                similarEnabled={similarBehavior}
                setSimilarEnabled={setSimilarBehavior}
              />
            </Box>

            {/* Keywords Counter */}
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: loading ? 0.6 : 1 }}>
              <SkillWeighting
                title="Keyword Counter"
                attributeName="keyword"
                isWeightRequired={true}
                extractedSkill={extractedKeywords}
                setExtractedSkill={setExtractedKeywords}
                weightingEnabled={keywordWeightage}
                setWeightingEnabled={setKeywordWeightage}
                similarEnabled={keywordToggle}
                setSimilarEnabled={setKeywordToggle}
              />
            </Box>
          </Box>
        </Box>
      </Stack>
    </Drawer>
  );
};
